'use client';

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-resources-to-backend';
import {
  defaultLanguage,
  type Language,
  supportedLanguages,
} from './i18n-config';

let initPromise: Promise<typeof i18n> | null = null;

let initialLanguage: string = defaultLanguage;

/**
 * Sets the language that initI18n will use when it first initializes.
 * Must be called before any component calls useI18n() — intended to be
 * called from ServerLanguageProvider, which wraps the entire app tree.
 */
export function setInitialLanguage(language: string) {
  if (supportedLanguages.includes(language as Language)) {
    initialLanguage = language;
  }
}

/**
 * @todo
 * expore future RUM APIs to profile sensitive timings
 * startAction: https://datadoghq.dev/browser-sdk/interfaces/_datadog_browser-rum.DatadogRum.html#startaction
 * startResource: https://github.com/DataDog/browser-sdk/pull/4110
 */
export function initI18n(): Promise<typeof i18n> {
  // Already initialized with the correct language — nothing to do
  if (i18n.isInitialized && i18n.language === initialLanguage) {
    return Promise.resolve(i18n);
  }

  // Initialized with a stale language (e.g., different request on the server
  // where the module singleton persists). Switch to the correct language.
  // Defer changeLanguage to a microtask so its synchronous subscriber
  // notifications don't trigger setState in sibling components during render.
  if (i18n.isInitialized) {
    initPromise = new Promise<typeof i18n>((resolve) => {
      queueMicrotask(() => {
        i18n.changeLanguage(initialLanguage).then(() => resolve(i18n));
      });
    });

    return initPromise;
  }

  // Initialization already in progress — return the existing promise
  if (initPromise) return initPromise;

  // First initialization
  // eslint-disable-next-line import/no-named-as-default-member
  initPromise = i18n
    .use(
      Backend(
        (language: string, namespace: string) =>
          import(`../generated/i18n/locales/${language}/${namespace}.json`),
      ),
    )
    .use(initReactI18next)
    .init({
      lng: initialLanguage,
      fallbackLng: defaultLanguage,
      supportedLngs: supportedLanguages,
      ns: ['general'],
      defaultNS: 'general',
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      debug: false,
    })
    .then(() => {
      return i18n;
    });

  return initPromise;
}

/**
 * Hook to use with Suspense boundary for loading translations
 * Throws a promise that Suspense can catch during initialization
 */
export function useI18n(): typeof i18n {
  if (i18n.isInitialized && i18n.language === initialLanguage) return i18n;

  throw initI18n();
}
